import React, { useEffect, useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@radix-ui/react-popover';
import { FormControl } from './form';
import { Button } from './button';
import { cn } from '@/lib/utils';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from './command';
import { ControllerRenderProps, UseFormReturn } from 'react-hook-form';
import { Check, ChevronsUpDown } from 'lucide-react';

interface TimezonePickerProps {
  form: UseFormReturn<any>;
  field: ControllerRenderProps<any>;
  className?: string;
  onChange?: (timezone: string) => void;
}

const TimezonePicker: React.FC<TimezonePickerProps> = ({ form, field, className = '', onChange }) => {
  const [timezones, setTimezones] = useState<string[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Get all supported timezones

    // @ts-expect-error SupportedValuesOf is not supported for this ts version
    const tzs = Intl.supportedValuesOf('timeZone');
    setTimezones(tzs);
  }, []);

  const formatTimezoneName = (timezone: string) => {
    try {
      const date = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'long',
        hour: 'numeric',
        minute: 'numeric',
      });
      return `${timezone} (${formatter.format(date)})`;
    } catch {
      return timezone;
    }
  };

  return (
    <div className={className}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <FormControl>
            <Button
              variant="outline"
              role="combobox"
              className={cn('w-full justify-between', !field.value && 'text-muted-foreground')}
            >
              {field.value
                ? timezones.find((language) => language === field.value)
                : 'Select timezone'}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </FormControl>
        </PopoverTrigger>
        <PopoverContent className="z-50 w-[--radix-popover-trigger-width] p-0">
          <Command>
            <CommandInput placeholder="Search timezone..." />
            <CommandList>
              <CommandEmpty>No timezone found.</CommandEmpty>
              <CommandGroup>
                {timezones.map((timezone) => (
                  <CommandItem
                    value={timezone}
                    key={timezone}
                    onSelect={() => {
                      form.setValue('timeZone', timezone);
                      setOpen(false);
                      onChange?.(timezone);
                    }}
                  >
                    {formatTimezoneName(timezone)}
                    <Check
                      className={cn(
                        'ml-auto',
                        timezone === field.value ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default TimezonePicker;
